/**
 * useShipmentLiveStatus — 사이드바 "출하조회 LIVE" 마커의 점등 여부.
 *
 * 오늘(KST) `daily_shipments/{today}` 집계 문서를 실시간 구독해 백엔드 푸시 알림
 * (shipmentNotifyOnUpdate)과 같은 기준으로 판정한다.
 *   - 시작   : 실적(gov_m3 + cust_m3) > 0
 *   - 마무리 : 예정(planned) > 0 이고 잔량(planned − shipped) ≤ 0
 *   - 점등   : 시작했고 아직 마무리 전 → "live"
 *   - 소등   : 출하 없음 · 마무리됨 · 감시 창(07~18시) 밖 → "off"
 *
 * 18시 이후 소등은 rqcWatchToday(5분 감시)가 도는 창과 맞춘 것 — 예정 대비 잔량이
 * 남은 채 하루가 끝나면(취소 등) 밤새 빨간 불이 켜져 있는 걸 막는다.
 *
 * 집계 문서는 rqcWatchToday(평일 07~18시 5분) + 출하조회 라이브 조회가 갱신하므로
 * 화면을 아무도 안 봐도 최대 5분 지연으로 따라온다.
 *
 * 권한: daily_shipments read 게이트(shipment|dispatch|stats)와 정합 — 게이트 밖이면
 * 구독하지 않고 "off". (마커 자체가 그 메뉴 권한이 있을 때만 보이므로 실질 영향 없음)
 *
 * 법인(2026-10-20 1단계): daily_shipments 는 중앙 데이터 → 중앙 공간에서만 구독한다.
 * 경원 공간(출하조회 메뉴에 같은 LIVE 마커가 붙음)·내 업무(사이드바에 출하조회 없음)에서는 구독하지 않고 "off".
 * 2단계에서 경원 출하가 연결되면 tenants/{법인}/daily_shipments 를 그 법인 공간에서 구독하도록 바꾼다.
 */
import { useContext, useEffect, useMemo, useState } from "react";
import { dailyShipmentsRepo } from "@/services/daily_shipments.repository";
import { useAuth } from "@/hooks/useAuth";
import { kstToday } from "@/domain/rqc";
import type { DailyShipment } from "@/domain/daily_shipments";
import { SpaceContext } from "@/contexts/space-context";
import { LEGACY_TENANT, isTenantDataConnected } from "@/domain/space";

export type ShipmentLiveStatus = "live" | "off";

/** 소등 시각(KST, 시) — rqcWatchToday 감시 창 종료와 동일 */
const OFF_AFTER_HOUR = 18;
/** 날짜·시각 재평가 주기 — 자정 넘김·18시 경계를 잡기 위한 타이머 */
const TICK_MS = 60 * 1000;

function kstHour(): number {
  return new Date(Date.now() + 9 * 60 * 60 * 1000).getUTCHours();
}

/** 집계 1건 → 점등 여부 (순수 함수, 테스트·재사용용) */
export function resolveShipmentLive(
  row: DailyShipment | null,
  hour: number = kstHour(),
): ShipmentLiveStatus {
  if (!row) return "off";
  if (hour >= OFF_AFTER_HOUR) return "off";
  const round1 = (n: number) => Math.round(n * 10) / 10;
  const shipped = round1(row.total_m3);
  const planned = round1(row.total_planned_m3 ?? 0);
  if (shipped <= 0) return "off";
  const finished = planned > 0 && planned - shipped <= 0;
  return finished ? "off" : "live";
}

interface Snapshot {
  /** 이 스냅샷이 속한 날짜 — 자정 넘김 직후 옛 문서가 섞이지 않게 구분 */
  date: string;
  row: DailyShipment | null;
}

export function useShipmentLiveStatus(): ShipmentLiveStatus {
  const { hasPerm } = useAuth();
  // 공간 밖(Provider 없음)이면 옛 동작(중앙 기준) 그대로
  const sp = useContext(SpaceContext);
  const tenant = sp ? sp.tenant : LEGACY_TENANT;
  const allowed =
    isTenantDataConnected(tenant) &&
    !!tenant &&
    (hasPerm("shipment.view", tenant) || hasPerm("dispatch.view", tenant) || hasPerm("stats.view", tenant));

  // 1분 틱 — 자정(날짜 교체)·18시(소등) 경계를 재평가하는 유일한 시계
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- tick 이 바뀔 때마다 오늘 날짜 재계산
  const today = useMemo(() => kstToday(), [tick]);

  const [snap, setSnap] = useState<Snapshot | null>(null);

  // 오늘 집계 문서 구독 (권한 있을 때만)
  useEffect(() => {
    if (!allowed) return;
    return dailyShipmentsRepo.watchByDate(
      today,
      (row) => setSnap({ date: today, row }),
      () => setSnap({ date: today, row: null }),
    );
  }, [allowed, today]);

  // 파생: 권한 없음 · 날짜 불일치(자정 직후) → off, 그 외 규칙 판정
  return useMemo(() => {
    if (!allowed || !snap || snap.date !== today) return "off";
    return resolveShipmentLive(snap.row);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tick: 18시 경계 재평가
  }, [allowed, snap, today, tick]);
}
